import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Helmet } from "react-helmet";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import SmoothScroll from "@/components/SmoothScroll";
import Navigation from "@/components/Navigation";
import TextureOverlay from "@/components/TextureOverlay";
import BackToTop from "@/components/BackToTop";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import Research from "@/pages/Research";
import ResearchDetailPage from "@/pages/ResearchDetailPage";
import Notes from "@/pages/Notes";
import NotePage from "@/pages/NotePage";
import About from "@/pages/About";
import CVContact from "@/pages/CVContact";
import PolicyPage from "@/pages/PolicyPage";
import NotFound from "@/pages/NotFound";

// Lazy-load admin pages (not needed for public visitors)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminSetup = lazy(() => import("@/pages/admin/AdminSetup"));
const NoteEditor = lazy(() => import("@/pages/admin/NoteEditor"));
const ProjectEditor = lazy(() => import("@/pages/admin/ProjectEditor"));
const PageEditor = lazy(() => import("@/pages/admin/PageEditor"));
const MediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const PolicyEditor = lazy(() => import("@/pages/admin/PolicyEditor"));
const TranslateTest = lazy(() => import("@/pages/admin/TranslateTest"));

// Admin route prefix - from environment variable
const ADMIN_PREFIX = process.env.REACT_APP_ADMIN_PREFIX || "/admin";

// Layout wrapper that conditionally shows public site chrome
const PublicLayout = ({ children }) => (
  <SmoothScroll>
    <div className="App relative min-h-screen flex flex-col" data-testid="app-container">
      <TextureOverlay />
      <Navigation />
      <BackToTop />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  </SmoothScroll>
);

// Admin layout without public site chrome - includes noindex meta
// Always forced LTR + English typography (admin never changes with language toggle)
const AdminLayout = ({ children }) => (
  <div className="App relative admin-layout" dir="ltr" data-testid="admin-container">
    <Helmet>
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <meta name="googlebot" content="noindex, nofollow" />
    </Helmet>
    {children}
  </div>
);

// Router component to handle layout switching
const AppRouter = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith(ADMIN_PREFIX);

  if (isAdminRoute) {
    return (
      <AdminLayout>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            <Route path={ADMIN_PREFIX} element={<AdminLogin />} />
            <Route path={`${ADMIN_PREFIX}/setup`} element={<AdminSetup />} />
            <Route path={`${ADMIN_PREFIX}/translate-test`} element={<TranslateTest />} />
            <Route path={`${ADMIN_PREFIX}/dashboard`} element={
              <ProtectedRoute><AdminDashboard /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/pages/:slug`} element={
              <ProtectedRoute><PageEditor /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/notes/new`} element={
              <ProtectedRoute><NoteEditor /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/notes/edit/:id`} element={
              <ProtectedRoute><NoteEditor /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/projects/new`} element={
              <ProtectedRoute><ProjectEditor /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/projects/edit/:id`} element={
              <ProtectedRoute><ProjectEditor /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/media`} element={
              <ProtectedRoute><MediaLibrary /></ProtectedRoute>
            } />
            <Route path={`${ADMIN_PREFIX}/policies/edit/:id`} element={
              <ProtectedRoute><PolicyEditor /></ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </AdminLayout>
    );
  }

  return (
    <PublicLayout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/research" element={<Research />} />
        <Route path="/research/:slug" element={<ResearchDetailPage />} />
        <Route path="/notes" element={<Notes />} />
        <Route path="/notes/:slug" element={<NotePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/cv" element={<CVContact />} />
        <Route path="/privacy-policy" element={<PolicyPage />} />
        <Route path="/cookie-policy" element={<PolicyPage />} />
        <Route path="/terms" element={<PolicyPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PublicLayout>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
